import { useEffect, useMemo, useState } from "react";
import { Bell, Mail, Webhook } from "lucide-react";
import type { FormAutomationConfig } from "@colab/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useAutosave, type SaveStatus } from "@/hooks/use-autosave";
import { useUpdateFormAutomation } from "./use-update-form-automation";

// Automation tab — configure side effects that run after submission. Three
// kind cards (Slack / Email / Webhook), each with an enable toggle plus
// kind-specific inputs. Single entry per kind in v1 UI (matches legacy).

export function AutomationConfigForm({
  formId,
  automation,
  onSaveStatusChange,
}: {
  formId: string;
  automation: FormAutomationConfig;
  onSaveStatusChange?: (status: SaveStatus) => void;
}) {
  const mutation = useUpdateFormAutomation();
  const [local, setLocal] = useState<FormAutomationConfig>(automation);

  useEffect(() => {
    setLocal(automation);
  }, [automation]);

  const autosave = useAutosave<FormAutomationConfig>({
    onFlush: async (next) => {
      await mutation.mutateAsync({ id: formId, automation: next });
    },
    wait: 500,
  });

  useEffect(() => {
    onSaveStatusChange?.(autosave.status);
  }, [autosave.status, onSaveStatusChange]);

  // Find existing entries per kind. The UI is single-entry per kind so we
  // dedupe by the first match — duplicates from legacy data round-trip
  // intact via the existing `others` array.
  const slack = useMemo(
    () => local.on_submit.find((a) => a.kind === "slack") ?? null,
    [local],
  );
  const email = useMemo(
    () => local.on_submit.find((a) => a.kind === "email") ?? null,
    [local],
  );
  const webhook = useMemo(
    () => local.on_submit.find((a) => a.kind === "webhook") ?? null,
    [local],
  );

  function setAction(
    kind: "slack" | "email" | "webhook",
    action: FormAutomationConfig["on_submit"][number] | null,
  ) {
    const others = local.on_submit.filter((a) => a.kind !== kind);
    const next: FormAutomationConfig = {
      on_submit: action ? [...others, action] : others,
    };
    setLocal(next);
    autosave.queue(next);
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ActionCard
        icon={Bell}
        title="Slack notification"
        description="Post to a channel when a submission lands."
        enabled={!!slack}
        onToggle={(on) => {
          if (on) setAction("slack", { kind: "slack", channel: "#partner-ops" });
          else setAction("slack", null);
        }}
      >
        {slack && slack.kind === "slack" && (
          <div className="space-y-1.5">
            <Label htmlFor="slack-channel">Channel</Label>
            <Input
              id="slack-channel"
              placeholder="#partner-ops"
              value={slack.channel}
              onChange={(e) =>
                setAction("slack", { kind: "slack", channel: e.target.value })
              }
              maxLength={200}
            />
            <p className="text-[11px] text-muted-foreground">
              Use a channel name or ID. The bot must already be invited.
            </p>
          </div>
        )}
      </ActionCard>

      <ActionCard
        icon={Mail}
        title="Email recipients"
        description="Notify one or more inboxes."
        enabled={!!email}
        onToggle={(on) => {
          if (on) setAction("email", { kind: "email", to: [] });
          else setAction("email", null);
        }}
      >
        {email && email.kind === "email" && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="email-to">Recipients</Label>
              <Input
                id="email-to"
                placeholder="ops@bookboost.io, sales@bookboost.io"
                value={email.to.join(", ")}
                onChange={(e) => {
                  const list = e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);
                  setAction("email", { ...email, to: list });
                }}
              />
              <p className="text-[11px] text-muted-foreground">
                Comma-separated. Max 20 recipients.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email-subject">Subject (optional)</Label>
              <Input
                id="email-subject"
                placeholder="New form submission"
                value={email.subject ?? ""}
                maxLength={200}
                onChange={(e) =>
                  setAction("email", {
                    ...email,
                    subject: e.target.value || undefined,
                  })
                }
              />
            </div>
          </>
        )}
      </ActionCard>

      <ActionCard
        icon={Webhook}
        title="Webhook"
        description="POST submission JSON to an external URL."
        enabled={!!webhook}
        onToggle={(on) => {
          if (on) setAction("webhook", { kind: "webhook", url: "" });
          else setAction("webhook", null);
        }}
      >
        {webhook && webhook.kind === "webhook" && (
          <div className="space-y-1.5">
            <Label htmlFor="webhook-url">URL</Label>
            <Input
              id="webhook-url"
              placeholder="https://example.com/hook"
              value={webhook.url}
              onChange={(e) =>
                setAction("webhook", { kind: "webhook", url: e.target.value })
              }
              maxLength={2000}
            />
            <p className="text-[11px] text-muted-foreground">
              HTTPS only. Retries + signing land with the dispatcher.
            </p>
          </div>
        )}
      </ActionCard>
    </div>
  );
}

function ActionCard({
  icon: Icon,
  title,
  description,
  enabled,
  onToggle,
  children,
}: {
  icon: typeof Bell;
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (next: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <header className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-sm font-semibold tracking-tight">
            {title}
          </h3>
          <p className="text-[11px] text-muted-foreground">{description}</p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={onToggle}
          aria-label={`Toggle ${title}`}
        />
      </header>
      {enabled && (
        <>
          <Separator className="my-3" />
          <div className="space-y-3">{children}</div>
        </>
      )}
    </div>
  );
}
